/**
 * Seller photo coach gates — Appendix A defaults; admin-tunable via system_settings.
 */

export type ListingPhotoGateMode = "block" | "warn" | "off";

export type ListingPhotoCoachSettings = {
  listing_photo_min_short_edge_px: number;
  listing_photo_min_dimension_mode: ListingPhotoGateMode;
  listing_photo_quality_check_mode: ListingPhotoGateMode;
};

export const DEFAULT_LISTING_PHOTO_MIN_SHORT_EDGE_PX = 1080;
export const DEFAULT_LISTING_PHOTO_MIN_DIMENSION_MODE: ListingPhotoGateMode =
  "block";
export const DEFAULT_LISTING_PHOTO_QUALITY_CHECK_MODE: ListingPhotoGateMode =
  "block";

export const LISTING_PHOTO_GATE_MODES: ListingPhotoGateMode[] = [
  "block",
  "warn",
  "off",
];

export function isListingPhotoGateMode(
  value: unknown,
): value is ListingPhotoGateMode {
  return value === "block" || value === "warn" || value === "off";
}

export function resolveListingPhotoCoachSettings(
  raw: Partial<ListingPhotoCoachSettings> | null | undefined,
): ListingPhotoCoachSettings {
  const minEdge = raw?.listing_photo_min_short_edge_px;
  return {
    listing_photo_min_short_edge_px:
      typeof minEdge === "number" &&
      Number.isFinite(minEdge) &&
      minEdge >= 480 &&
      minEdge <= 4096
        ? Math.round(minEdge)
        : DEFAULT_LISTING_PHOTO_MIN_SHORT_EDGE_PX,
    listing_photo_min_dimension_mode: isListingPhotoGateMode(
      raw?.listing_photo_min_dimension_mode,
    )
      ? raw.listing_photo_min_dimension_mode
      : DEFAULT_LISTING_PHOTO_MIN_DIMENSION_MODE,
    listing_photo_quality_check_mode: isListingPhotoGateMode(
      raw?.listing_photo_quality_check_mode,
    )
      ? raw.listing_photo_quality_check_mode
      : DEFAULT_LISTING_PHOTO_QUALITY_CHECK_MODE,
  };
}

let coachSettingsCache: {
  settings: ListingPhotoCoachSettings;
  expiry: number;
} | null = null;

const COACH_SETTINGS_CACHE_MS = 5 * 60 * 1000;

/** Clears cached coach settings (e.g. after admin save in same session). */
export function invalidateListingPhotoCoachSettingsCache(): void {
  coachSettingsCache = null;
}

/** Public read path for upload validation — falls back to Appendix A defaults. */
export async function fetchListingPhotoCoachSettings(): Promise<ListingPhotoCoachSettings> {
  const now = Date.now();
  if (coachSettingsCache && now < coachSettingsCache.expiry) {
    return coachSettingsCache.settings;
  }

  try {
    const response = await fetch("/api/settings/system", {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    });

    if (!response.ok) {
      return resolveListingPhotoCoachSettings(undefined);
    }

    const data = await response.json();
    const settings = resolveListingPhotoCoachSettings(
      (data.settings ?? data) as Partial<ListingPhotoCoachSettings>,
    );
    coachSettingsCache = { settings, expiry: now + COACH_SETTINGS_CACHE_MS };
    return settings;
  } catch {
    return resolveListingPhotoCoachSettings(undefined);
  }
}
